import { supabase } from './supabase';

export type TodoItemStatus = 'open' | 'completed';
export type TodoItemPriority = 'none' | 'low' | 'medium' | 'high';

export interface TodoList {
  id: string;
  business_id: string;
  name: string;
  is_default: boolean;
  position: number;
  is_archived: boolean;
  created_by_resource_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TodoItem {
  id: string;
  business_id: string;
  list_id: string;
  title: string;
  description: string | null;
  status: TodoItemStatus;
  priority: TodoItemPriority;
  due_date: string | null;
  due_time: string | null;
  customer_id: string | null;
  assignee_resource_id: string | null;
  created_by_resource_id: string | null;
  position: number;
  completed_at: string | null;
  is_deleted: boolean;
  estimated_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export interface TodoItemWithRelations extends TodoItem {
  list?: Pick<TodoList, 'id' | 'name' | 'is_default'> | null;
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  assignee?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
}

export interface CreateTodoItemInput {
  title: string;
  description?: string | null;
  list_id?: string | null;
  due_date?: string | null;
  due_time?: string | null;
  customer_id?: string | null;
  assignee_resource_id?: string | null;
  priority?: TodoItemPriority;
  /** Optional required time to complete (minutes). Used by AI when blocking calendar Events. */
  estimated_minutes?: number | null;
  created_by_resource_id?: string | null;
}

export interface UpdateTodoItemInput {
  title?: string;
  description?: string | null;
  list_id?: string;
  status?: TodoItemStatus;
  priority?: TodoItemPriority;
  due_date?: string | null;
  due_time?: string | null;
  customer_id?: string | null;
  assignee_resource_id?: string | null;
  position?: number;
  estimated_minutes?: number | null;
}

export interface FindTodoItemsFilters {
  /** Default: open only. Pass 'all' to include completed. */
  status?: TodoItemStatus | 'all';
  list_id?: string;
  assignee_resource_id?: string;
  customer_id?: string;
  /** Words matched against title (ilike). */
  query?: string;
  /** YYYY-MM-DD — items due on this date */
  due_date?: string;
  /** Include items with due_date before this date (YYYY-MM-DD) that are still open */
  overdue_before?: string;
  include_deleted?: boolean;
  /** Open items whose due_date is today or earlier */
  actually_due?: boolean;
  /** Scope to what this viewer can see (assigned to, created by, or visible list). */
  visible_to?: TodoViewer;
  limit?: number;
}

/**
 * Who is looking at Tasks. Everyone sees their own world:
 * lists they created, lists shared with them, and tasks assigned to them.
 */
export interface TodoViewer {
  /** Real resources row id — null for a business owner with no resource row. */
  resourceId: string | null;
  /**
   * Admins own the legacy business Inbox and any list without a creator.
   * Non-admin resources get their own per-person Inbox.
   */
  isAdmin: boolean;
}

/** Which Inbox this viewer uses: admins keep the legacy business Inbox (null owner). */
export function todoInboxOwnerId(viewer: TodoViewer): string | null {
  return viewer.isAdmin ? null : viewer.resourceId;
}

/** True when this viewer can see the given list (own, admin-legacy, or member). */
export function isListVisibleToViewer(
  list: Pick<TodoList, 'id' | 'created_by_resource_id'>,
  viewer: TodoViewer,
  memberListIds: Set<string>
): boolean {
  if (viewer.isAdmin && list.created_by_resource_id === null) return true;
  if (viewer.resourceId && list.created_by_resource_id === viewer.resourceId) return true;
  return memberListIds.has(list.id);
}

const TODO_ITEM_SELECT = `
  *,
  list:todo_lists!todo_items_list_id_fkey(id, name, is_default),
  customer:customers!todo_items_customer_id_fkey(
    id, first_name, last_name, email, phone
  ),
  assignee:resources!todo_items_assignee_resource_id_fkey(
    id, first_name, last_name, avatar_url
  )
`;

function requireSupabase() {
  if (!supabase) throw new Error('Supabase client is not initialized');
  return supabase;
}

function normalizeTitle(title: string): string {
  return title.trim();
}

function normalizeDueTime(time: string | null | undefined): string | null {
  if (time == null || time === '') return null;
  const trimmed = time.trim();
  if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  throw new Error('due_time must be HH:MM or HH:MM:SS');
}

function validateDue(dueDate?: string | null, dueTime?: string | null) {
  if (dueTime && !dueDate) {
    throw new Error('due_time requires due_date');
  }
  if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    throw new Error('due_date must be YYYY-MM-DD');
  }
}

function throwQueryError(error: { message?: string } | null, fallback: string): never {
  throw new Error(error?.message || fallback);
}

/**
 * Owners often have a synthetic currentResource.id (auth user id) that is NOT
 * in resources — never pass that into created_by_resource_id (FK would fail).
 */
async function resolveResourceFk(
  businessId: string,
  resourceId: string | null | undefined
): Promise<string | null> {
  const id = String(resourceId || '').trim();
  if (!id) return null;
  const client = requireSupabase();
  const { data, error } = await client
    .from('resources')
    .select('id')
    .eq('id', id)
    .eq('business_id', businessId)
    .maybeSingle();
  if (error) throwQueryError(error, 'Failed to validate team member');
  return data?.id ?? null;
}

/**
 * Ensure the Inbox exists; returns its id. Pass inboxOwnerResourceId for a
 * per-person Inbox (null = legacy business/admin Inbox).
 */
export async function ensureDefaultTodoList(
  businessId: string,
  inboxOwnerResourceId: string | null = null
): Promise<string> {
  const client = requireSupabase();
  const { data, error } = await client.rpc('ensure_default_todo_list', {
    p_business_id: businessId,
    p_resource_id: inboxOwnerResourceId,
  });
  if (error) throw error;
  if (!data) throw new Error('Failed to ensure default Inbox list');
  return data as string;
}

let todoListsCache: { key: string; at: number; lists: TodoList[] } | null = null;

function todoListsCacheKey(businessId: string, viewer?: TodoViewer): string {
  return viewer
    ? `${businessId}:${viewer.resourceId ?? 'null'}:${viewer.isAdmin ? 1 : 0}`
    : `${businessId}:all`;
}

export function invalidateTodoListsCache(businessId?: string): void {
  if (!businessId || todoListsCache?.key.startsWith(`${businessId}:`)) {
    todoListsCache = null;
  }
}

/** List ids this resource is a member of (shared lists). */
export async function getTodoListMembershipIds(
  businessId: string,
  resourceId: string
): Promise<string[]> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('todo_list_members')
    .select('list_id')
    .eq('business_id', businessId)
    .eq('resource_id', resourceId);
  if (error) throw error;
  return (data || []).map((r) => r.list_id as string);
}

export async function getTodoLists(
  businessId: string,
  options: { includeArchived?: boolean; skipCache?: boolean; viewer?: TodoViewer } = {}
): Promise<TodoList[]> {
  const client = requireSupabase();
  const cacheKey = todoListsCacheKey(businessId, options.viewer);
  const useCache = !options.includeArchived && !options.skipCache;
  if (
    useCache &&
    todoListsCache &&
    todoListsCache.key === cacheKey &&
    Date.now() - todoListsCache.at < 20_000
  ) {
    return todoListsCache.lists;
  }

  await ensureDefaultTodoList(
    businessId,
    options.viewer ? todoInboxOwnerId(options.viewer) : null
  );

  let query = client
    .from('todo_lists')
    .select('*')
    .eq('business_id', businessId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });

  if (!options.includeArchived) {
    query = query.eq('is_archived', false);
  }

  const { data, error } = await query;
  if (error) throw error;
  let lists = (data || []) as TodoList[];

  if (options.viewer) {
    const viewer = options.viewer;
    const memberIds = viewer.resourceId
      ? new Set(await getTodoListMembershipIds(businessId, viewer.resourceId))
      : new Set<string>();
    lists = lists.filter((l) => isListVisibleToViewer(l, viewer, memberIds));
  }

  if (useCache) {
    todoListsCache = { key: cacheKey, at: Date.now(), lists };
  }
  return lists;
}

export async function createTodoList(
  businessId: string,
  name: string,
  options: { created_by_resource_id?: string | null } = {}
): Promise<TodoList> {
  const client = requireSupabase();
  const trimmed = name.trim();
  if (!trimmed) throw new Error('List name is required');

  await ensureDefaultTodoList(businessId);

  const lists = await getTodoLists(businessId, { skipCache: true });
  const createdBy = await resolveResourceFk(businessId, options.created_by_resource_id);

  // Case-insensitive match among the SAME creator's lists — different people
  // can each have their own "Groceries" without colliding.
  const existingMatch = lists.find(
    (l) =>
      !l.is_default &&
      l.created_by_resource_id === createdBy &&
      l.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (existingMatch) return existingMatch;

  const nextPosition =
    lists.reduce((max, l) => (l.position > max ? l.position : max), -1) + 1;

  const { data, error } = await client
    .from('todo_lists')
    .insert({
      business_id: businessId,
      name: trimmed,
      is_default: false,
      position: nextPosition,
      created_by_resource_id: createdBy,
    })
    .select()
    .single();

  if (error) throwQueryError(error, 'Failed to create list');
  invalidateTodoListsCache(businessId);
  return data as TodoList;
}

export async function updateTodoList(
  businessId: string,
  listId: string,
  updates: { name?: string; position?: number; is_archived?: boolean }
): Promise<TodoList> {
  const client = requireSupabase();
  const patch: Record<string, unknown> = {};

  if (updates.name !== undefined) {
    const trimmed = updates.name.trim();
    if (!trimmed) throw new Error('List name is required');
    patch.name = trimmed;
  }
  if (updates.position !== undefined) patch.position = updates.position;
  if (updates.is_archived !== undefined) {
    if (updates.is_archived) {
      const { data: list } = await client
        .from('todo_lists')
        .select('is_default')
        .eq('id', listId)
        .eq('business_id', businessId)
        .maybeSingle();
      if (list?.is_default) {
        throw new Error('Cannot archive the default Inbox list');
      }
    }
    patch.is_archived = updates.is_archived;
  }

  if (Object.keys(patch).length === 0) {
    throw new Error('Nothing to update');
  }

  const { data, error } = await client
    .from('todo_lists')
    .update(patch)
    .eq('id', listId)
    .eq('business_id', businessId)
    .select()
    .single();

  if (error) throw error;
  return data as TodoList;
}

/**
 * Delete a non-default list. Open + completed items are moved to the list
 * creator's Inbox first (legacy business Inbox when the creator is unknown).
 */
export async function deleteTodoList(businessId: string, listId: string): Promise<void> {
  const client = requireSupabase();

  const { data: list, error: listError } = await client
    .from('todo_lists')
    .select('id, is_default, created_by_resource_id')
    .eq('id', listId)
    .eq('business_id', businessId)
    .maybeSingle();

  if (listError) throw listError;
  if (!list) throw new Error('List not found');
  if (list.is_default) throw new Error('Cannot delete the default Inbox list');

  // Admin creators use the legacy business Inbox, not a per-person one.
  let inboxOwner: string | null = list.created_by_resource_id ?? null;
  if (inboxOwner) {
    const { data: creator } = await client
      .from('resources')
      .select('is_admin')
      .eq('id', inboxOwner)
      .maybeSingle();
    if (creator?.is_admin) inboxOwner = null;
  }
  const inboxId = await ensureDefaultTodoList(businessId, inboxOwner);

  const { error: moveError } = await client
    .from('todo_items')
    .update({ list_id: inboxId })
    .eq('business_id', businessId)
    .eq('list_id', listId)
    .eq('is_deleted', false);

  if (moveError) throw moveError;

  const { error } = await client
    .from('todo_lists')
    .delete()
    .eq('id', listId)
    .eq('business_id', businessId);

  if (error) throw error;
}

export async function createTodoItem(
  businessId: string,
  input: CreateTodoItemInput,
  viewer?: TodoViewer
): Promise<TodoItemWithRelations> {
  const client = requireSupabase();
  const title = normalizeTitle(input.title);
  if (!title) throw new Error('Title is required');

  validateDue(input.due_date, input.due_time);
  const dueTime = normalizeDueTime(input.due_time);

  // No explicit list → the viewer's own Inbox (legacy business Inbox otherwise).
  const listId =
    input.list_id ||
    (await ensureDefaultTodoList(businessId, viewer ? todoInboxOwnerId(viewer) : null));

  const { data: list, error: listError } = await client
    .from('todo_lists')
    .select('id')
    .eq('id', listId)
    .eq('business_id', businessId)
    .eq('is_archived', false)
    .maybeSingle();

  if (listError) throwQueryError(listError, 'Failed to load list');
  if (!list) throw new Error('List not found');

  const { data: maxPos } = await client
    .from('todo_items')
    .select('position')
    .eq('list_id', listId)
    .eq('is_deleted', false)
    .eq('status', 'open')
    .order('position', { ascending: false })
    .limit(1);

  const position = (maxPos?.[0]?.position ?? -1) + 1;
  const createdBy = await resolveResourceFk(businessId, input.created_by_resource_id);
  const assigneeId = await resolveResourceFk(businessId, input.assignee_resource_id);

  let estimatedMinutes: number | null = null;
  if (input.estimated_minutes !== undefined && input.estimated_minutes !== null) {
    const mins = Number(input.estimated_minutes);
    if (!Number.isFinite(mins) || mins < 1 || mins > 1440) {
      throw new Error('estimated_minutes must be between 1 and 1440');
    }
    estimatedMinutes = Math.round(mins);
  }

  const { data, error } = await client
    .from('todo_items')
    .insert({
      business_id: businessId,
      list_id: listId,
      title,
      description: input.description?.trim() || null,
      status: 'open',
      priority: input.priority || 'none',
      due_date: input.due_date || null,
      due_time: dueTime,
      customer_id: input.customer_id || null,
      assignee_resource_id: assigneeId,
      created_by_resource_id: createdBy,
      position,
      estimated_minutes: estimatedMinutes,
    })
    .select(TODO_ITEM_SELECT)
    .single();

  if (error) throwQueryError(error, 'Failed to create task');
  return data as TodoItemWithRelations;
}

export async function getTodoItem(
  businessId: string,
  itemId: string
): Promise<TodoItemWithRelations | null> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('todo_items')
    .select(TODO_ITEM_SELECT)
    .eq('id', itemId)
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .maybeSingle();

  if (error) throw error;
  return (data as TodoItemWithRelations) || null;
}

export async function findTodoItems(
  businessId: string,
  filters: FindTodoItemsFilters = {}
): Promise<TodoItemWithRelations[]> {
  const client = requireSupabase();
  const limit = Math.min(Math.max(filters.limit ?? 100, 1), 200);

  let query = client
    .from('todo_items')
    .select(TODO_ITEM_SELECT)
    .eq('business_id', businessId)
    .order('due_date', { ascending: true, nullsFirst: false })
    .order('position', { ascending: true })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (!filters.include_deleted) {
    query = query.eq('is_deleted', false);
  }

  const status = filters.status ?? 'open';
  if (status !== 'all') {
    query = query.eq('status', status);
  }

  if (filters.list_id) query = query.eq('list_id', filters.list_id);
  if (filters.assignee_resource_id) {
    query = query.eq('assignee_resource_id', filters.assignee_resource_id);
  }
  if (filters.customer_id) query = query.eq('customer_id', filters.customer_id);
  if (filters.due_date) query = query.eq('due_date', filters.due_date);
  if (filters.overdue_before) {
    query = query.lt('due_date', filters.overdue_before).eq('status', 'open');
  }
  if (filters.actually_due) {
    query = query
      .eq('status', 'open')
      .not('due_date', 'is', null)
      .lte('due_date', localTodayIso());
  }
  if (filters.query?.trim()) {
    query = query.ilike('title', `%${filters.query.trim()}%`);
  }

  if (filters.visible_to) {
    const conds = await viewerVisibilityConds(businessId, filters.visible_to);
    if (conds.length === 0) return [];
    query = query.or(conds.join(','));
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as TodoItemWithRelations[];
}

export function localTodayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** True when an open task's due date is today or earlier. */
export function isActuallyDue(
  dueDate: string | null | undefined,
  status: TodoItemStatus = 'open'
): boolean {
  if (!dueDate || status !== 'open') return false;
  return dueDate <= localTodayIso();
}

/**
 * Time-aware overdue check for red "Overdue" styling. The Today bucket is
 * date-based (a task due today is on today's plate all day), but a task only
 * reads as OVERDUE once its moment has actually passed:
 *   - due date before today → overdue
 *   - due today with a due time → overdue once that time passes
 *   - due today with no time → never overdue today (rolls over at midnight)
 */
export function isTodoOverdue(item: {
  due_date: string | null;
  due_time?: string | null;
  status: TodoItemStatus | string;
}): boolean {
  if (!item.due_date || item.status !== 'open') return false;
  const today = localTodayIso();
  if (item.due_date < today) return true;
  if (item.due_date > today || !item.due_time) return false;
  const now = new Date();
  const nowHm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return item.due_time.slice(0, 5) <= nowHm;
}

/** Open tasks whose due date is today or earlier — for Control Centre badge. */
export async function countDueTodoItems(
  businessId: string,
  viewer?: TodoViewer
): Promise<number> {
  const client = requireSupabase();
  const today = localTodayIso();
  let query = client
    .from('todo_items')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .eq('status', 'open')
    .not('due_date', 'is', null)
    .lte('due_date', today);

  if (viewer) {
    const conds = await viewerVisibilityConds(businessId, viewer);
    if (conds.length === 0) return 0;
    query = query.or(conds.join(','));
  }

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export async function updateTodoItem(
  businessId: string,
  itemId: string,
  updates: UpdateTodoItemInput
): Promise<TodoItemWithRelations> {
  const client = requireSupabase();
  const patch: Record<string, unknown> = {};

  if (updates.title !== undefined) {
    const title = normalizeTitle(updates.title);
    if (!title) throw new Error('Title is required');
    patch.title = title;
  }
  if (updates.description !== undefined) {
    patch.description = updates.description?.trim() || null;
  }
  if (updates.list_id !== undefined) {
    const { data: list, error: listError } = await client
      .from('todo_lists')
      .select('id')
      .eq('id', updates.list_id)
      .eq('business_id', businessId)
      .eq('is_archived', false)
      .maybeSingle();
    if (listError) throw listError;
    if (!list) throw new Error('List not found');
    patch.list_id = updates.list_id;
  }
  if (updates.status !== undefined) {
    if (updates.status !== 'open' && updates.status !== 'completed') {
      throw new Error('status must be open or completed');
    }
    patch.status = updates.status;
  }
  if (updates.priority !== undefined) patch.priority = updates.priority;
  if (updates.estimated_minutes !== undefined) {
    if (updates.estimated_minutes === null) {
      patch.estimated_minutes = null;
    } else {
      const mins = Number(updates.estimated_minutes);
      if (!Number.isFinite(mins) || mins <= 0 || mins > 24 * 60) {
        throw new Error('estimated_minutes must be between 1 and 1440');
      }
      patch.estimated_minutes = Math.round(mins);
    }
  }
  if (updates.position !== undefined) patch.position = updates.position;
  if (updates.due_date !== undefined || updates.due_time !== undefined) {
    let nextDate = updates.due_date;
    let nextTime = updates.due_time;
    if (updates.due_date === undefined || updates.due_time === undefined) {
      const { data: current } = await client
        .from('todo_items')
        .select('due_date, due_time')
        .eq('id', itemId)
        .eq('business_id', businessId)
        .maybeSingle();
      if (updates.due_date === undefined) nextDate = current?.due_date ?? null;
      if (updates.due_time === undefined) nextTime = current?.due_time ?? null;
    }
    if (nextDate === null) nextTime = null;
    validateDue(nextDate ?? null, nextTime ?? null);
    patch.due_date = nextDate ?? null;
    patch.due_time = normalizeDueTime(nextTime ?? null);
  }
  if (updates.customer_id !== undefined) patch.customer_id = updates.customer_id;
  if (updates.assignee_resource_id !== undefined) {
    patch.assignee_resource_id = updates.assignee_resource_id;
  }
  if (updates.position !== undefined) patch.position = updates.position;

  if (Object.keys(patch).length === 0) {
    throw new Error('Nothing to update');
  }

  const { data, error } = await client
    .from('todo_items')
    .update(patch)
    .eq('id', itemId)
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .select(TODO_ITEM_SELECT)
    .single();

  if (error) throw error;

  if (patch.status === 'completed') {
    try {
      const { syncLinkedEventOnTodoCompleted } = await import('./calendarEvents');
      await syncLinkedEventOnTodoCompleted(businessId, itemId);
    } catch (syncErr) {
      console.warn('[todos] linked Event complete failed:', syncErr);
    }
  }

  return data as TodoItemWithRelations;
}

export async function completeTodoItem(
  businessId: string,
  itemId: string
): Promise<TodoItemWithRelations> {
  return updateTodoItem(businessId, itemId, { status: 'completed' });
}

export async function reopenTodoItem(
  businessId: string,
  itemId: string
): Promise<TodoItemWithRelations> {
  return updateTodoItem(businessId, itemId, { status: 'open' });
}

/** Soft-delete a task (always retrievable with include_deleted). */
export async function deleteTodoItem(businessId: string, itemId: string): Promise<void> {
  const client = requireSupabase();
  const { error } = await client
    .from('todo_items')
    .update({ is_deleted: true })
    .eq('id', itemId)
    .eq('business_id', businessId)
    .eq('is_deleted', false);

  if (error) throw error;
}

export interface BulkDeleteTodoScope {
  /** Restrict to one list. Required unless status is 'completed'. */
  listId?: string;
  /** 'open' | 'completed' | 'all' (default 'all'). */
  status?: 'open' | 'completed' | 'all';
  /** Limit to this viewer's visible world (list-less "clear completed"). */
  visibleTo?: TodoViewer;
}

/** OR-condition string limiting a todo_items query to a viewer's visible world. */
async function viewerVisibilityConds(
  businessId: string,
  viewer: TodoViewer
): Promise<string[]> {
  const visibleLists = await getTodoLists(businessId, { viewer });
  const conds: string[] = [];
  if (viewer.resourceId) {
    conds.push(`assignee_resource_id.eq.${viewer.resourceId}`);
    conds.push(`created_by_resource_id.eq.${viewer.resourceId}`);
  }
  if (visibleLists.length > 0) {
    conds.push(`list_id.in.(${visibleLists.map((l) => l.id).join(',')})`);
  }
  return conds;
}

/** Count tasks matching a bulk-delete scope (for previews/confirmation). */
export async function countTodoItemsForBulkDelete(
  businessId: string,
  scope: BulkDeleteTodoScope
): Promise<number> {
  const client = requireSupabase();
  let q = client
    .from('todo_items')
    .select('id', { count: 'exact', head: true })
    .eq('business_id', businessId)
    .eq('is_deleted', false);
  if (scope.listId) q = q.eq('list_id', scope.listId);
  if (scope.status && scope.status !== 'all') q = q.eq('status', scope.status);
  if (scope.visibleTo) {
    const conds = await viewerVisibilityConds(businessId, scope.visibleTo);
    if (conds.length === 0) return 0;
    q = q.or(conds.join(','));
  }
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

/**
 * Soft-delete MANY tasks at once (all tasks in a list, all completed, etc.).
 * Guard: without a list the scope must be completed-only — never mass-delete
 * every open task across all lists in one call. Returns how many were deleted.
 */
export async function bulkDeleteTodoItems(
  businessId: string,
  scope: BulkDeleteTodoScope
): Promise<number> {
  if (!scope.listId && scope.status !== 'completed') {
    throw new Error('Bulk delete without a list is only allowed for completed tasks');
  }
  const client = requireSupabase();
  let q = client
    .from('todo_items')
    .update({ is_deleted: true })
    .eq('business_id', businessId)
    .eq('is_deleted', false);
  if (scope.listId) q = q.eq('list_id', scope.listId);
  if (scope.status && scope.status !== 'all') q = q.eq('status', scope.status);
  if (scope.visibleTo) {
    const conds = await viewerVisibilityConds(businessId, scope.visibleTo);
    if (conds.length === 0) return 0;
    q = q.or(conds.join(','));
  }
  const { data, error } = await q.select('id');
  if (error) throw error;
  return (data || []).length;
}

// ── List sharing (todo_list_members) ─────────────────────────────────────────

export interface TodoListMember {
  id: string;
  list_id: string;
  resource_id: string;
  resource?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
}

/** People a list is shared with (excluding the creator, who is implicit). */
export async function getTodoListMembers(
  businessId: string,
  listId: string
): Promise<TodoListMember[]> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('todo_list_members')
    .select(
      `id, list_id, resource_id,
       resource:resources!todo_list_members_resource_id_fkey(id, first_name, last_name, avatar_url)`
    )
    .eq('business_id', businessId)
    .eq('list_id', listId);
  if (error) throw error;
  return (data || []) as unknown as TodoListMember[];
}

/** Replace a list's member set (adds missing, removes unchecked). */
export async function setTodoListMembers(
  businessId: string,
  listId: string,
  resourceIds: string[],
  addedByResourceId?: string | null
): Promise<void> {
  const client = requireSupabase();
  const wanted = Array.from(new Set(resourceIds.filter(Boolean)));

  const { data: existing, error: readError } = await client
    .from('todo_list_members')
    .select('id, resource_id')
    .eq('business_id', businessId)
    .eq('list_id', listId);
  if (readError) throw readError;

  const current = new Set((existing || []).map((m) => m.resource_id as string));
  const toAdd = wanted.filter((id) => !current.has(id));
  const toRemove = (existing || []).filter((m) => !wanted.includes(m.resource_id as string));

  if (toRemove.length > 0) {
    const { error } = await client
      .from('todo_list_members')
      .delete()
      .in('id', toRemove.map((m) => m.id as string));
    if (error) throw error;
  }

  if (toAdd.length > 0) {
    const addedBy = await resolveResourceFk(businessId, addedByResourceId);
    const { error } = await client.from('todo_list_members').insert(
      toAdd.map((resourceId) => ({
        business_id: businessId,
        list_id: listId,
        resource_id: resourceId,
        added_by_resource_id: addedBy,
      }))
    );
    if (error) throw error;
  }

  invalidateTodoListsCache(businessId);
}
